/**
 * Decision Factories
 *
 * Convenient constructors for the three availability states.
 * These set sensible defaults for shape and interaction so consumers
 * only need to specify what differs from the default.
 */

import type {
  DefaultExplanationCode,
  FeatureDecision,
  FeatureInteraction,
  FeatureShape,
  ScopeExplanation,
} from './types';

export function createAvailableDecision<TCode extends string = DefaultExplanationCode>(params?: {
  shape?: FeatureShape;
  interaction?: FeatureInteraction;
  explanation?: ScopeExplanation<TCode>;
}): FeatureDecision<TCode> {
  return {
    availability: 'available',
    shape: params?.shape ?? 'default',
    interaction: params?.interaction ?? 'full',
    explanation: params?.explanation,
  };
}

export function createBlockedDecision<TCode extends string = DefaultExplanationCode>(params: {
  explanation: ScopeExplanation<TCode>;
  shape?: FeatureShape;
  interaction?: FeatureInteraction;
}): FeatureDecision<TCode> {
  return {
    availability: 'blocked',
    shape: params.shape ?? 'default',
    interaction: params.interaction ?? 'view-only',
    explanation: params.explanation,
  };
}

export function createHiddenDecision<TCode extends string = DefaultExplanationCode>(params?: {
  explanation?: ScopeExplanation<TCode>;
  shape?: FeatureShape;
  interaction?: FeatureInteraction;
}): FeatureDecision<TCode> {
  return {
    availability: 'hidden',
    shape: params?.shape ?? 'default',
    interaction: params?.interaction ?? 'view-only',
    explanation: params?.explanation,
  };
}
